// INSIDO (15-camara-blur): pure logic for camera background blur.
//
// Kept free of app imports (no aliases, no Solid, no livekit at module load)
// so it can be unit-tested in node by the Playwright contract specs. The
// processor library (@livekit/track-processors + MediaPipe) is only loaded on
// demand, the first time blur is turned on, so it never weighs on app start.
import type { Track as TrackNs, TrackProcessor } from "livekit-client";

/**
 * Background blur intensity. "off" is the default and means no processor.
 */
export type BackgroundBlurLevel = "off" | "light" | "strong";

/** Every level, in the order they are offered to the user. */
export const BACKGROUND_BLUR_LEVELS: readonly BackgroundBlurLevel[] = [
  "off",
  "light",
  "strong",
];

/** Level used when the call button turns blur on without a stored preference. */
export const DEFAULT_ACTIVE_BLUR_LEVEL: Exclude<BackgroundBlurLevel, "off"> =
  "light";

/**
 * Blur radius (px, in the processor's WebGL blur) for each active level.
 * INSIDO (18-feedback-teste): raised from 6/18 — at 720p a 6 px blur on a real
 * scene was barely visible; 14 reads as a clear "soft" background, 32 as the
 * strong one.
 */
export const BLUR_RADIUS: Record<
  Exclude<BackgroundBlurLevel, "off">,
  number
> = {
  light: 14,
  strong: 32,
};

/** Processor name, used to recognise our processor on a track. */
export const BLUR_PROCESSOR_NAME = "insido-background-blur";

/**
 * Whether a value (e.g. read back from persisted settings) is a valid level.
 */
export function isBackgroundBlurLevel(
  value: unknown,
): value is BackgroundBlurLevel {
  return (
    typeof value === "string" &&
    (BACKGROUND_BLUR_LEVELS as readonly string[]).includes(value)
  );
}

/** Normalise any persisted value to a valid level (falls back to "off"). */
export function normaliseBlurLevel(value: unknown): BackgroundBlurLevel {
  return isBackgroundBlurLevel(value) ? value : "off";
}

/** Blur radius for a level, or undefined when blur is off. */
export function blurRadiusFor(level: BackgroundBlurLevel): number | undefined {
  return level === "off" ? undefined : BLUR_RADIUS[level];
}

/**
 * Level the call button switches to: off when blur is active, otherwise the
 * last active level the user chose (or the default).
 */
export function toggledBlurLevel(
  current: BackgroundBlurLevel,
  lastActive?: BackgroundBlurLevel,
): BackgroundBlurLevel {
  if (current !== "off") return "off";
  return lastActive && lastActive !== "off"
    ? lastActive
    : DEFAULT_ACTIVE_BLUR_LEVEL;
}

/**
 * The browser capabilities the blur processor needs. Mirrors the checks of
 * `@livekit/track-processors` (BackgroundTransformer.isSupported +
 * ProcessorWrapper.hasModernApiSupport) without loading the library.
 *
 * We deliberately require the modern MediaStreamTrackProcessor/Generator path
 * (Chromium / Electron): the canvas fallback the library offers on
 * Firefox/Safari is much heavier on CPU, so there the feature stays hidden.
 */
export interface BlurSupportEnv {
  hasOffscreenCanvas: boolean;
  hasVideoFrame: boolean;
  hasCreateImageBitmap: boolean;
  hasTrackProcessor: boolean;
  hasTrackGenerator: boolean;
  /** Lazily probe WebGL2 (creates a throwaway canvas). */
  hasWebGL2: () => boolean;
}

/** Decide support from a capability snapshot. */
export function isBlurSupportedIn(env: BlurSupportEnv): boolean {
  return (
    env.hasOffscreenCanvas &&
    env.hasVideoFrame &&
    env.hasCreateImageBitmap &&
    env.hasTrackProcessor &&
    env.hasTrackGenerator &&
    env.hasWebGL2()
  );
}

/** Capability snapshot of the current global scope (browser or node). */
export function currentBlurSupportEnv(
  scope: Record<string, unknown> = globalThis as unknown as Record<
    string,
    unknown
  >,
): BlurSupportEnv {
  const has = (name: string) => typeof scope[name] !== "undefined";
  return {
    hasOffscreenCanvas: has("OffscreenCanvas"),
    hasVideoFrame: has("VideoFrame"),
    hasCreateImageBitmap: has("createImageBitmap"),
    hasTrackProcessor: has("MediaStreamTrackProcessor"),
    hasTrackGenerator: has("MediaStreamTrackGenerator"),
    hasWebGL2: () => {
      try {
        const doc = scope.document as Document | undefined;
        return !!doc?.createElement("canvas").getContext("webgl2");
      } catch {
        return false;
      }
    },
  };
}

let cachedSupport: boolean | undefined;

/**
 * Whether background blur can run here. Memoised: the WebGL2 probe creates a
 * context, which we only want to do once per page.
 */
export function isBackgroundBlurSupported(): boolean {
  if (cachedSupport === undefined) {
    cachedSupport = isBlurSupportedIn(currentBlurSupportEnv());
  }
  return cachedSupport;
}

/** The subset of `@livekit/track-processors` this module uses. */
export interface TrackProcessorsModule {
  supportsBackgroundProcessors: () => boolean;
  BackgroundProcessor: (
    options: { mode: "background-blur"; blurRadius?: number },
    name?: string,
  ) => BlurProcessor;
}

/** A video processor that can change its blur radius in place. */
export type BlurProcessor = TrackProcessor<TrackNs.Kind.Video> & {
  switchTo?: (options: {
    mode: "background-blur";
    blurRadius?: number;
  }) => Promise<void>;
};

const loadTrackProcessors = () =>
  import("@livekit/track-processors") as unknown as Promise<TrackProcessorsModule>;

/**
 * Build a blur processor for an active level. The library is loaded on demand
 * (injectable for tests).
 */
export async function createBlurProcessor(
  level: Exclude<BackgroundBlurLevel, "off">,
  loader: () => Promise<TrackProcessorsModule> = loadTrackProcessors,
): Promise<BlurProcessor> {
  const lib = await loader();
  if (!lib.supportsBackgroundProcessors()) {
    throw new Error("background blur is not supported in this browser");
  }
  return lib.BackgroundProcessor(
    { mode: "background-blur", blurRadius: BLUR_RADIUS[level] },
    BLUR_PROCESSOR_NAME,
  );
}

/** The bits of a LiveKit LocalVideoTrack the blur logic touches. */
export interface BlurTarget {
  getProcessor(): TrackProcessor<TrackNs.Kind> | undefined;
  setProcessor(processor: BlurProcessor): Promise<void>;
  stopProcessor(): Promise<void>;
}

/**
 * Bring a camera track to the requested level without republishing it:
 * - off: stop our processor (if any);
 * - same processor present: switch its radius in place;
 * - otherwise: attach a fresh processor.
 * Returns the level actually in effect afterwards.
 */
export async function applyBlurLevel(
  track: BlurTarget,
  level: BackgroundBlurLevel,
  factory: (
    level: Exclude<BackgroundBlurLevel, "off">,
  ) => Promise<BlurProcessor> = createBlurProcessor,
): Promise<BackgroundBlurLevel> {
  const current = track.getProcessor() as BlurProcessor | undefined;
  const ours = current?.name === BLUR_PROCESSOR_NAME;

  if (level === "off") {
    if (ours) await track.stopProcessor();
    return "off";
  }

  if (ours && typeof current?.switchTo === "function") {
    await current.switchTo({
      mode: "background-blur",
      blurRadius: BLUR_RADIUS[level],
    });
    return level;
  }

  await track.setProcessor(await factory(level));
  return level;
}
